import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import type { SelectProps } from 'components/common';
import { CommonSelect, PlaceholderNoAccess } from 'components/common';
import { type SelectFlowsParams, useInfiniteFlows } from '../api';
import type { CreativeTextDetails, Flow } from '../types';
import { useInView } from 'react-intersection-observer';
import { useFormContext } from 'react-hook-form';
import type { SelectChangeEvent } from '@mui/material';
import { usePermissionGuard } from 'features/login';

interface SelectFlowsAttrs {
  open: boolean;
  multiples: boolean;
  placeholder: string;
  params?: SelectFlowsParams;
  creativeText?: CreativeTextDetails;
  name: 'default_flows_ids' | 'additional_flows_ids';
}

type SelectFlowsProps = Partial<
  Pick<SelectProps, 'handleChange' | 'inputLabel' | 'onClose'> &
  SelectFlowsAttrs
>;

const SelectCreativeTextFlowsComponent: FC<SelectFlowsProps> =
  ({
     handleChange,
     name,
     inputLabel,
     multiples,
     placeholder,
     onClose,
     open,
     params,
     creativeText,
   }) => {
    const { ref, inView } = useInView({
      threshold: 0,
    });
    const { watch, setValue } = useFormContext();
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
      useInfiniteFlows({ params });

    // Watch both fields to handle mutual exclusivity
    const defaultFlowsIds: number[] = watch('default_flows_ids');
    const additionalFlowsIds: number[] = watch('additional_flows_ids');

    useEffect(() => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Handle mutual exclusivity when selection changes
    const handleSelectionChange = (event: SelectChangeEvent<unknown>) => {
      const selectedIds = event.target.value as number[];

      if (name === 'default_flows_ids') {
        // Remove selected default flows from additional flows
        const newAdditionalFlows = additionalFlowsIds.filter(
          (id) => !selectedIds.includes(id),
        );
        setValue('additional_flows_ids', newAdditionalFlows);
      } else {
        // Remove selected additional flows from default flows
        const newDefaultFlows = defaultFlowsIds.filter(
          (id) => !selectedIds.includes(id),
        );
        setValue('default_flows_ids', newDefaultFlows);
      }
      handleChange?.(event);
    };

    const options = useMemo(() => {
      if (!data) return [];
      const pages = data.pages.flatMap((page) =>
        page.data.map(({ name, id }: Flow) => ({
          label: name,
          value: id,
        })),
      );

      // Get flows from creative text based on the field name
      const creativeTextFlows = creativeText
        ? name === 'default_flows_ids'
          ? creativeText.default_flows
          : creativeText.additional_flows
        : [];

      // Get flows that are not selected in the other field
      const availableFlows =
        name === 'default_flows_ids'
          ? pages.filter(({ value }) => !additionalFlowsIds.includes(value))
          : pages.filter(({ value }) => !defaultFlowsIds.includes(value));

      // Get creative text flows that are not in pages and not in the other field
      const newCreativeTextFlows = creativeTextFlows
        .filter((flow) => {
          const isInPages = pages.some(({ value }) => value === flow.id);
          const isInOtherField =
            name === 'default_flows_ids'
              ? additionalFlowsIds.includes(flow.id)
              : defaultFlowsIds.includes(flow.id);
          return !isInPages && !isInOtherField;
        })
        .map((flow) => ({
          label: flow.name,
          value: flow.id,
        }));

      return [...availableFlows, ...newCreativeTextFlows];
    }, [data, creativeText, name, defaultFlowsIds, additionalFlowsIds]);

    return (
      <CommonSelect
        name={name ?? 'flows'}
        inputLabel={inputLabel ?? 'Flows'}
        options={options}
        handleChange={handleSelectionChange}
        placeholder={placeholder}
        multiples={multiples}
        onClose={onClose}
        open={open}
        loading={isFetchingNextPage}
        loadMoreRef={ref}
      />
    );
  };

const SelectCreativeTextFlows = (props: SelectFlowsProps) => {
  const { accessToViewFlow } = usePermissionGuard();
  if (!accessToViewFlow) return <PlaceholderNoAccess title={'No access to view flow'} />;

  return (
    <SelectCreativeTextFlowsComponent
      {...props}
    />
  );
};

export default SelectCreativeTextFlows;
